import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { ImageCropModal, StandardModalActionButton, StandardModalDescription, StandardModalFormGroup, StandardModalLabel, StandardModalSection, VideoCropModal } from "src/components";
import { TaxonomyEditorModal } from "src/components/TaxonomyEditorModal";
import { deleteCategory, s3PresignedCategoryUrl, saveCategory, updateCategory } from "../../api";
import { VISIBILITY_GROUP } from "../../constants";
import { isBase64Image, isBase64Video } from "../utils/VideoUtils";
import UploadMedia from "../forms/UploadMedia";
import { ASPECT_RATIO_LABEL } from "src/scenes/Noticeboard/constants";
import { Toast } from "src/components";
import { getVisibilityOptions } from "../../hooks/getVisibilityLabel";

const isVideoUrl = (url) => [".mp4", ".mov", ".webm", ".ogg", ".mkv", ".avi", ".flv"].some(extension => url?.toLowerCase().includes(extension));

const CategoryMediaGrid = styled.div`
    display: grid;
    grid-template-columns: 11.625rem minmax(0, 20.75rem);
    justify-content: start;
    gap: 1.25rem;

    @media (max-width: 640px) {
        grid-template-columns: 1fr;
    }
`;

export function CategoryEditor({ open, onClose, category, featureName }) {
    const isEditing = Boolean(category?.uuid);
    const visibilityOptions = getVisibilityOptions();
    const categoryUploadRef = useRef(null);
    const coverUploadRef = useRef(null);
    const [name, setName] = useState("");
    const [visibleTo, setVisibleTo] = useState(VISIBILITY_GROUP.EMPLOYEES);
    const [categoryImage, setCategoryImage] = useState(null);
    const [categoryImageBlob, setCategoryImageBlob] = useState(null);
    const [coverFile, setCoverFile] = useState(null);
    const [coverImageBlob, setCoverImageBlob] = useState(null);
    const [croppedVideo, setCroppedVideo] = useState(null);
    const [coverFileType, setCoverFileType] = useState("image");
    const [cropData, setCropData] = useState({});
    const [cropTarget, setCropTarget] = useState(null);
    const [videoToCrop, setVideoToCrop] = useState(null);
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        setName(category?.name || "");
        setVisibleTo(category?.visibleTo || VISIBILITY_GROUP.EMPLOYEES);
        setCategoryImage(category?.iconUrl || null);
        setCategoryImageBlob(null);
        setCoverFile(category?.headerVideoUrl || null);
        setCoverImageBlob(null);
        setCroppedVideo(null);
        setCoverFileType(isVideoUrl(category?.headerVideoUrl) ? "video" : "image");
        setCropData({});
        setErrors({});
    }, [category, open]);

    const setImage = (image) => {
        setCategoryImage(image);
        setErrors(current => ({ ...current, categoryImage: "" }));
        if (isBase64Image(image)) setCropTarget({ src: image, type: "category" });
    };

    const setCover = (file) => {
        setCoverFile(file);
        setErrors(current => ({ ...current, coverFile: "" }));
        setCoverImageBlob(null);
        setCroppedVideo(null);
        setCropData({});
        if (isBase64Image(file)) setCropTarget({ src: file, type: "cover" });
        if (isBase64Video(file)) setVideoToCrop(file);
    };

    const completeImageCrop = (file) => {
        if (!file || !cropTarget) return;
        const preview = URL.createObjectURL(file);
        if (cropTarget.type === "category") {
            setCategoryImage(preview);
            setCategoryImageBlob(file);
        } else {
            setCoverFile(preview);
            setCoverImageBlob(file);
        }
        setCropTarget(null);
    };

    const removeCover = () => {
        setCoverFile(null);
        setCoverImageBlob(null);
        setCroppedVideo(null);
        setCropData({});
        setCoverFileType("image");
        coverUploadRef.current?.triggerFilePicker();
    };

    const validate = () => {
        const nextErrors = {};
        if (!name.trim()) nextErrors.name = "Category name is required.";
        if (!categoryImage) nextErrors.categoryImage = "Category image is required.";
        if (!coverFile) nextErrors.coverFile = "Cover image or video is required.";
        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const uploadNewMedia = async (payload) => {
        if (categoryImageBlob) {
            const response = await s3PresignedCategoryUrl(categoryImageBlob, categoryImageBlob.type.split("/").pop(), "image");
            payload.iconUrl = response.storedUrl.split("?")[0];
        }
        if (croppedVideo) {
            const response = await s3PresignedCategoryUrl(croppedVideo, croppedVideo.type.split("/").pop(), "video");
            payload.videoKey = response.uuid;
            payload.headerVideoUrl = response.storedUrl.split("?")[0];
            payload.crop = {
                x: cropData.crop?.croppedAreaPixels?.x ?? 0,
                y: cropData.crop?.croppedAreaPixels?.y ?? 0,
                width: cropData.crop?.croppedAreaPixels?.width ?? 0,
                height: cropData.crop?.croppedAreaPixels?.height ?? 0
            };
            payload.trimStart = cropData.trimStart ?? 0;
            payload.trimEnd = cropData.trimEnd ?? 0;
            payload.aspectRatio = cropData.aspectRatio || ASPECT_RATIO_LABEL.WIDESCREEN;
        } else if (coverImageBlob) {
            const response = await s3PresignedCategoryUrl(coverImageBlob, coverImageBlob.type.split("/").pop(), "image");
            payload.videoKey = response.uuid;
            payload.headerVideoUrl = response.storedUrl.split("?")[0];
            payload.videoThumbnailUrl = payload.headerVideoUrl;
        }
    };

    const save = async () => {
        if (!validate()) return;
        setIsLoading(true);
        try {
            const payload = {
                name: name.trim(),
                visibleTo,
                iconUrl: category?.iconUrl,
                videoKey: category?.videoKey,
                headerVideoUrl: category?.headerVideoUrl
            };
            await uploadNewMedia(payload);
            if (isEditing) await updateCategory(payload, category.uuid);
            else await saveCategory(payload);
            Toast.success(`Category has been successfully ${isEditing ? "updated" : "added"}.`);
            onClose();
        } catch (error) {
            Toast.error(error.getApiErrorDetails().message);
        } finally {
            setIsLoading(false);
        }
    };

    const remove = async () => {
        if (!window.confirm("Deleting this category permanently deletes its subcategories and content. Continue?")) return;
        setIsLoading(true);
        try {
            await deleteCategory(category.uuid);
            Toast.success("Category deleted successfully.");
            onClose();
        } catch (error) {
            Toast.error(error.getApiErrorDetails().message);
        } finally {
            setIsLoading(false);
        }
    };

    return <>
        <TaxonomyEditorModal
            open={open}
            onClose={onClose}
            title={isEditing ? "Edit Category" : "Create New Category"}
            description={isEditing ? "Update your category image, cover media, name, and visibility." : `Add a custom category to ${featureName}.`}
            nameLabel={isEditing ? "Category Name" : "Create Category Name"}
            name={name}
            onNameChange={event => setName(event.target.value)}
            nameError={errors.name}
            visibilityOptions={visibilityOptions}
            visibleTo={visibleTo}
            onVisibleToChange={setVisibleTo}
            onSubmit={save}
            isLoading={isLoading}
            onDelete={isEditing ? remove : undefined}
        >
            <StandardModalSection>
                <CategoryMediaGrid>
                    <StandardModalFormGroup>
                        <StandardModalLabel>Upload Category Image</StandardModalLabel>
                        <UploadMedia ref={categoryUploadRef} isCircleDisplay onFileSelect={setImage} file={categoryImage} croppedFile={categoryImage} fileType="image" hideBrowseButton={Boolean(categoryImage)} boxStyle={{ width: "11.625rem", height: "11.625rem", borderRadius: "50%" }} />
                        {categoryImage && <StandardModalActionButton type="button" onClick={() => categoryUploadRef.current?.triggerFilePicker()} width="11.5rem" height="2.375rem">Replace Category Image</StandardModalActionButton>}
                        {errors.categoryImage && <StandardModalDescription color="#D64545">{errors.categoryImage}</StandardModalDescription>}
                    </StandardModalFormGroup>
                    <StandardModalFormGroup>
                        <StandardModalLabel>Upload Category Cover Image/Video</StandardModalLabel>
                        <UploadMedia ref={coverUploadRef} isCircleDisplay={false} onFileSelect={setCover} file={coverFile} croppedFile={coverFile} allFileType setFileType={setCoverFileType} fileType={coverFileType} cropperResults={cropData} hideBrowseButton={Boolean(coverFile)} boxStyle={{ width: "100%", height: "11.625rem", borderRadius: "0.75rem" }} />
                        {coverFile && <StandardModalActionButton type="button" onClick={removeCover} width="11.5rem" height="2.375rem">Replace Cover Media</StandardModalActionButton>}
                        {errors.coverFile && <StandardModalDescription color="#D64545">{errors.coverFile}</StandardModalDescription>}
                    </StandardModalFormGroup>
                </CategoryMediaGrid>
            </StandardModalSection>
        </TaxonomyEditorModal>
        {cropTarget && <ImageCropModal imageSrc={cropTarget.src} isOpen onClose={() => setCropTarget(null)} onCropComplete={completeImageCrop} aspectRatios={cropTarget.type === "cover" ? [{ width: 16, height: 9, label: "16:9", standardName: ASPECT_RATIO_LABEL.WIDESCREEN }] : [{ width: 1, height: 1, label: "1:1" }]} allowedOrientations={cropTarget.type === "cover" ? ["landscape"] : ["circle"]} />}
        {videoToCrop && <VideoCropModal videoSrc={videoToCrop} isOpen onClose={() => setVideoToCrop(null)} onCropComplete={(file, crop, trimStart, trimEnd, aspectRatio) => { setCroppedVideo(file); setCropData({ crop, trimStart, trimEnd, aspectRatio }); setVideoToCrop(null); }} aspectRatios={[{ width: 16, height: 9, label: "16:9", standardName: ASPECT_RATIO_LABEL.WIDESCREEN }]} allowedOrientations={["landscape"]} />}
    </>;
}
